import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, Database, Loader2, Send, User, X } from 'lucide-react';
import { auth } from '../../shared/api/firebase/config';

const API_BASE = (
  import.meta.env.VITE_RDA_API_BASE_URL ||
  import.meta.env.VITE_BACKEND_BASE_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_BACKEND_URL ||
  'https://rda-backend-thuq.onrender.com'
).replace(/\/+$/, '');

const getAuthHeaders = async () => {
  try {
    const token = await getToken();
    return token
      ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      : { 'Content-Type': 'application/json' };
  } catch {
    return { 'Content-Type': 'application/json' };
  }
};

const WELCOME_MESSAGE = {
  role: 'ai',
  text: 'Olá. Sou o Assistente Analítico RDA. Faça perguntas sobre este documento e responderei com base exclusiva no seu conteúdo.',
  chunks: [],
};

export default function GedSemanticChat({ driveFileId, onClose }) {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isIndexed, setIsIndexed] = useState(null);
  const [isIngesting, setIsIngesting] = useState(false);
  const bottomRef = useRef(null);
  const pollingRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Verifica status de indexação ao montar ou ao mudar o driveFileId
  useEffect(() => {
    if (!driveFileId) { setIsIndexed(null); return; }
    let active = true;
    setIsIndexed(null);
    getAuthHeaders().then((headers) =>
      fetch(`${API_BASE}/ged/status/${encodeURIComponent(driveFileId)}`, { headers })
        .then((r) => r.ok ? r.json() : Promise.reject(r.status))
        .then((data) => { if (active) setIsIndexed(Boolean(data.is_indexed)); })
        .catch(() => { if (active) setIsIndexed(false); })
    );
    return () => { active = false; };
  }, [driveFileId]);

  // Limpeza do polling ao desmontar
  useEffect(() => {
    return () => {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    };
  }, []);

  const handleStartIngest = useCallback(async () => {
    if (!driveFileId || isIngesting) return;
    setIsIngesting(true);
    try {
      const ingestHeaders = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/ged/ingest`, {
        method: 'POST',
        headers: ingestHeaders,
        body: JSON.stringify({ drive_file_id: driveFileId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Erro HTTP ${res.status}`);
      }
      // Ingestão iniciada em background — iniciar polling a cada 5s
      pollingRef.current = setInterval(async () => {
        try {
          const pollHeaders = await getAuthHeaders();
          const sr = await fetch(`${API_BASE}/ged/status/${encodeURIComponent(driveFileId)}`, { headers: pollHeaders });
          if (sr.ok) {
            const sd = await sr.json();
            if (sd.is_indexed) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              setIsIndexed(true);
              setIsIngesting(false);
            }
          }
        } catch { /* ignorar erros de polling */ }
      }, 5000);
    } catch (err) {
      setIsIngesting(false);
      setMessages((prev) => [
        ...prev,
        { role: 'ai', text: `⚠️ Falha ao iniciar ingestão: ${err.message}`, chunks: [], isError: true },
      ]);
    }
  }, [driveFileId, isIngesting]);

  const handleSendMessage = async () => {
    const question = input.trim();
    if (!question || isLoading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: question, chunks: [] }]);
    setIsLoading(true);

    try {
      const askHeaders = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/ged/ask`, {
        method: 'POST',
        headers: askHeaders,
        body: JSON.stringify({
          question,
          drive_file_id: driveFileId || null,
          top_k: 5,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Erro HTTP ${res.status}`);
      }

      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          text: data.answer || '(sem resposta)',
          chunks: data.chunks || [],
          modelUsed: data.model_used || '',
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          text: `⚠️ Não foi possível conectar ao Cérebro Analítico. Verifique se o servidor RDA está ativo.\n\nDetalhe: ${err.message}`,
          chunks: [],
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#1a1c2e] border-l border-slate-700/50">

      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-4 py-3 bg-[#20243B] border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <Bot size={18} className="text-blue-400" />
          <span className="text-white text-sm font-semibold tracking-wide">
            Análise Semântica RDA
          </span>
          {/* Badge de status de indexação */}
          {isIndexed === null && driveFileId && (
            <span className="flex items-center gap-1 text-xs text-slate-400">
              <Loader2 size={10} className="animate-spin" /> verificando
            </span>
          )}
          {isIndexed === true && (
            <span className="flex items-center gap-1 text-xs bg-green-900/40 text-green-400 border border-green-800/50 rounded px-1.5 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> Indexado
            </span>
          )}
          {isIndexed === false && !isIngesting && (
            <span className="flex items-center gap-1 text-xs bg-amber-900/40 text-amber-400 border border-amber-800/50 rounded px-1.5 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" /> Não Indexado
            </span>
          )}
          {isIngesting && (
            <span className="flex items-center gap-1 text-xs bg-blue-900/40 text-blue-400 border border-blue-800/50 rounded px-1.5 py-0.5">
              <Loader2 size={10} className="animate-spin" /> Processando…
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {driveFileId && (
            <span className="text-xs text-slate-400 font-mono truncate max-w-[120px]" title={driveFileId}>
              {driveFileId.slice(0, 10)}…
            </span>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-2 py-1.5 md:p-1 rounded-lg md:rounded bg-slate-700 md:bg-transparent text-slate-200 md:text-slate-400 hover:bg-slate-600 hover:text-white transition-colors touch-manipulation"
              title="Fechar IA — voltar ao PDF"
            >
              <X size={18} className="md:hidden" />
              <span className="text-xs font-medium md:hidden">← PDF</span>
              <X size={16} className="hidden md:block" />
            </button>
          )}
        </div>
      </div>

      {/* ── Estado: verificando indexação ── */}
      {isIndexed === null && driveFileId && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-500">
          <Loader2 size={28} className="animate-spin text-slate-600" />
          <p className="text-sm">Verificando status do documento…</p>
        </div>
      )}

      {/* ── Estado: não indexado — Empty State + CTA ── */}
      {isIndexed === false && (
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
            <Database size={26} className="text-slate-500" />
          </div>
          <div>
            <p className="text-slate-300 text-sm font-medium mb-1">
              Documento ainda não processado
            </p>
            <p className="text-slate-500 text-xs leading-relaxed">
              Este documento ainda não foi processado pela Inteligência Artificial.
              Inicie o processamento para poder fazer perguntas sobre o seu conteúdo.
            </p>
          </div>
          {!isIngesting ? (
            <button
              onClick={handleStartIngest}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Database size={14} />
              Iniciar Processamento Semântico
            </button>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={24} className="animate-spin text-blue-400" />
              <p className="text-xs text-slate-400 text-center max-w-[200px]">
                A extrair texto e gerar vetores semânticos no servidor…
              </p>
              <p className="text-xs text-slate-600">Verificando a cada 5 segundos</p>
            </div>
          )}
        </div>
      )}

      {/* ── Estado: indexado — interface de chat normal ── */}
      {isIndexed === true && (
        <>
          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-4 scrollbar-thin scrollbar-thumb-slate-700">
            {messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} />
            ))}
            {isLoading && (
              <div className="flex items-start gap-2">
                <div className="shrink-0 w-7 h-7 rounded-full bg-blue-800/60 flex items-center justify-center">
                  <Bot size={14} className="text-blue-300" />
                </div>
                <div className="flex items-center gap-2 bg-[#20243B] rounded-lg px-4 py-2.5 border border-slate-700/50">
                  <Loader2 size={14} className="text-blue-400 animate-spin" />
                  <span className="text-slate-400 text-sm">Consultando os documentos…</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="shrink-0 px-3 py-3 bg-[#20243B] border-t border-slate-700/50">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Faça uma pergunta sobre este documento…"
                rows={2}
                disabled={isLoading}
                className="flex-1 resize-none rounded-lg bg-slate-800 border border-slate-600 text-white text-sm px-3 py-2 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 transition-colors"
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !input.trim()}
                className="shrink-0 p-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white transition-colors"
                title="Enviar (Enter)"
              >
                <Send size={16} />
              </button>
            </div>
            <p className="text-xs text-slate-600 mt-1.5 text-center">
              Respostas baseadas exclusivamente no conteúdo do documento indexado.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div
        className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
          isUser ? 'bg-slate-600' : message.isError ? 'bg-red-900/60' : 'bg-blue-800/60'
        }`}
      >
        {isUser
          ? <User size={14} className="text-slate-300" />
          : <Bot size={14} className={message.isError ? 'text-red-400' : 'text-blue-300'} />
        }
      </div>

      {/* Bubble */}
      <div className={`max-w-[85%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        <div
          className={`rounded-lg px-3 py-2.5 text-sm leading-relaxed border ${
            isUser
              ? 'bg-slate-700 text-slate-100 border-slate-600'
              : message.isError
              ? 'bg-red-950/40 text-red-300 border-red-800/50'
              : 'bg-[#20243B] text-slate-200 border-slate-700/50'
          }`}
        >
          <p className="whitespace-pre-wrap">{message.text}</p>
        </div>

        {/* Chunk badges */}
        {!isUser && message.chunks && message.chunks.length > 0 && (
          <div className="flex flex-wrap gap-1 px-1">
            {message.chunks.map((chunk, i) => (
              <span
                key={i}
                title={`Chunk ${chunk.chunk_index ?? i} — distância: ${
                  chunk.distance != null ? chunk.distance.toFixed(4) : 'N/D'
                }`}
                className="inline-flex items-center gap-1 text-xs bg-blue-900/40 text-blue-300 border border-blue-800/50 rounded px-1.5 py-0.5"
              >
                📍 Trecho {chunk.chunk_index != null ? chunk.chunk_index + 1 : i + 1}
              </span>
            ))}
          </div>
        )}

        {/* Model tag */}
        {!isUser && message.modelUsed && (
          <span className="text-xs text-slate-600 px-1">via {message.modelUsed}</span>
        )}
      </div>
    </div>
  );
}
